import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", code: "", address: "", contact: "", active: true };

export default function LabCentres() {
  const { can } = useAuth();
  const manage = can("lab.centres.manage");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try { const { data } = await api.get("/lab/centres"); setItems(data.items); }
    catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setFormError(""); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setFormError(""); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError("Centre name is required."); return; }
    setBusy(true); setFormError("");
    try {
      if (editing) await api.patch(`/lab/centres/${editing.centre_id}`, form);
      else await api.post("/lab/centres", form);
      toast.success(editing ? "Centre updated." : "Centre added.");
      setOpen(false); load();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };
  const toggle = async (c) => { try { await api.patch(`/lab/centres/${c.centre_id}`, { active: !c.active }); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!can("lab.orders.view")) return <NoAccess what="Collection centres" />;

  return (
    <div data-testid="lab-centres-page">
      <PageHeader overline="Cases" title="Collection Centres"
        subtitle="Franchise or satellite sample-collection centres attached to your lab."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-centre-btn"><Plus className="h-4 w-4" /> Add centre</button>} />
      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="lab-centres-table" columns={["Centre", "Code", "Contact", "Address", "Status", manage ? "Actions" : ""]}
          rows={items} empty={<EmptyState title="No collection centres" subtitle="Add your first collection centre." testid="lab-centres-empty" />}
          renderRow={(c) => (
            <tr key={c.centre_id} data-testid={`lab-centre-row-${c.centre_id}`}>
              <Td className="font-medium text-slate-900">{c.name}</Td>
              <Td className="text-slate-600">{c.code || "—"}</Td>
              <Td className="text-slate-600">{c.contact || "—"}</Td>
              <Td className="text-slate-600">{c.address || "—"}</Td>
              <Td><span className={`text-xs font-medium ${c.active ? "text-emerald-600" : "text-slate-400"}`}>{c.active ? "Active" : "Inactive"}</span></Td>
              {manage && <Td>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(c)} className={ghostBtnCls} data-testid={`edit-centre-${c.centre_id}`}>Edit</button>
                  <button onClick={() => toggle(c)} className={ghostBtnCls} data-testid={`toggle-centre-${c.centre_id}`}>{c.active ? "Archive" : "Restore"}</button>
                </div>
              </Td>}
            </tr>
          )} />
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit centre" : "Add centre"} testid="lab-centre-modal">
        <form onSubmit={save} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Centre name *"><input className={inputCls} data-testid="centre-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Code"><input className={inputCls} data-testid="centre-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
            <Field label="Contact"><input className={inputCls} data-testid="centre-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></Field>
            <Field label="Address"><input className={inputCls} data-testid="centre-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="centre-active" /> Active</label>
          {formError && <p className="text-sm text-red-600" data-testid="centre-form-error">{formError}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
            <button disabled={busy} className={primaryBtnCls} data-testid="save-centre-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
